/**
 * Tema for Compass-visninger: leser diagramfargene fra CSS-variablene (--color-chart-*) slik at
 * lys, grå og mørk modus gjelder både native paneler (Recharts) og Vega-Lite i sandkassen.
 */
import { useEffect, useState } from 'react';

export interface ViewTheme {
    palette: string[];
    grid: string;
    axis: string;
    text: string;
    panelBg: string;
    good: string;
    bad: string;
    warning: string;
    neutral: string;
    mode: string;
}

const FALLBACK: ViewTheme = {
    palette: ['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#06b6d4', '#ec4899', '#6366f1'],
    grid: '#e5e7eb',
    axis: '#6b7280',
    text: '#333333',
    panelBg: '#ffffff',
    good: '#16a34a',
    bad: '#dc2626',
    warning: '#d97706',
    neutral: '#2563eb',
    mode: 'light'
};

export function readViewTheme(): ViewTheme {
    if (typeof window === 'undefined') return FALLBACK;
    const style = getComputedStyle(document.documentElement);
    const get = (name: string, fallback: string) => style.getPropertyValue(name).trim() || fallback;
    return {
        palette: [1, 2, 3, 4, 5, 6, 7, 8].map((i, idx) => get(`--color-chart-${i}`, FALLBACK.palette[idx])),
        grid: get('--color-chart-grid', FALLBACK.grid),
        axis: get('--color-chart-axis', FALLBACK.axis),
        text: get('--color-text-primary', FALLBACK.text),
        panelBg: get('--color-panel-bg', FALLBACK.panelBg),
        good: get('--color-chart-good', FALLBACK.good),
        bad: get('--color-chart-bad', FALLBACK.bad),
        warning: get('--color-chart-warning', FALLBACK.warning),
        neutral: get('--color-chart-neutral', FALLBACK.neutral),
        mode: document.documentElement.getAttribute('data-theme') || 'light'
    };
}

/** Følger temabytte (ThemeContext setter data-theme på <html>). */
export function useViewTheme(): ViewTheme {
    const [theme, setTheme] = useState<ViewTheme>(() => readViewTheme());
    useEffect(() => {
        const observer = new MutationObserver(() => setTheme(readViewTheme()));
        observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
        return () => observer.disconnect();
    }, []);
    return theme;
}

/** Fargen for en semantisk betegnelse (good/bad/warning/neutral). */
export function semanticColor(theme: ViewTheme, name?: string): string | undefined {
    switch (name) {
        case 'good': return theme.good;
        case 'bad': return theme.bad;
        case 'warning': return theme.warning;
        case 'neutral':
        case 'accent': return theme.neutral;
        default: return undefined;
    }
}
